import React, { useState } from "react";

// region External libraries
import { useApolloClient, useQuery } from "@apollo/client";
import { format } from "date-fns";
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";
// endregion External libraries
// region Helpers
import { CsvColumn, downloadCsv, toFileNameSlug } from "src/helpers/downloadCsv";
// endregion Helpers
// region Languages
import useTranslation from "@languages/useTranslation";
import { AnnouncementMessages, GlobalMessages } from "@languages/interfaces";
// endregion Languages
// region Libraries
import { FileDownload } from "@libraries/mui/icons";
// endregion Libraries
// region Atoms
import StateMessage from "@atoms/StateMessage";
// endregion Atoms
// region Molecules
import ModalFormDialog from "@molecules/ModalFormDialog";
// endregion Molecules
// region Store
import {
  AnnouncementInteraction,
  announcementInteractionsQuery,
  AnnouncementManagementRow,
  TAnnouncementInteractionsQuery
} from "src/store/graphql/groups/announcement/query.graphql";
// endregion Store
// region Styles
import * as Styled from "./styles";
// endregion Styles

// region Interfaces
interface AnnouncementInteractionsDialogProps {
  announcement: AnnouncementManagementRow | null;
  onClose: () => void;
}
// endregion Interfaces

// region Constants
// Server-side cap of the interaction listing; the export walks the pages.
const PAGE_SIZE = 50;

// Safety stop for the export: 200 pages of 50 is 10k people, well above the
// reach of any announcement, and keeps a runaway loop from hammering the API.
// Hitting it is reported to the user instead of silently short-changing the file.
const EXPORT_MAX_PAGES = 200;

const formatOrDash = (value: string | null) => (
  value ? format(new Date(value), "dd/MM/yyyy HH:mm") : "—"
);
// endregion Constants

/**
 * "Who read / who clicked" listing of one announcement, across every tenant
 * (staff-gated server-side), with a CSV export of the whole listing.
 */
const AnnouncementInteractionsDialog: React.FC<AnnouncementInteractionsDialogProps> = ({
  announcement,
  onClose
}) => {

  // region Hooks
  const { t } = useTranslation();
  const { addToast } = useToast();
  const client = useApolloClient();
  // endregion Hooks

  // region States
  const [exporting, setExporting] = useState(false);
  // endregion States

  // region Queries
  const { data, loading } = useQuery<TAnnouncementInteractionsQuery>(announcementInteractionsQuery, {
    variables: { announcementId: announcement?.id, page: 1, perPage: PAGE_SIZE },
    skip: !announcement,
    fetchPolicy: "network-only"
  });
  // endregion Queries

  // region Constants
  const page = data?.announcement?.getAnnouncementInteractions;
  const records = page?.records ?? [];
  const totalCount = page?.totalCount ?? 0;

  const columns: CsvColumn<AnnouncementInteraction>[] = [
    { header: t(AnnouncementMessages.interactionsUser), value: (row) => row.userName },
    { header: t(AnnouncementMessages.interactionsEmail), value: (row) => row.userEmail },
    { header: t(AnnouncementMessages.interactionsTenant), value: (row) => row.tenantName },
    { header: t(AnnouncementMessages.interactionsReadAt), value: (row) => formatOrDash(row.readAt) },
    { header: t(AnnouncementMessages.interactionsClickedAt), value: (row) => formatOrDash(row.clickedAt) }
  ];
  // endregion Constants

  // region Handlers
  /**
   * Walks every page of the listing, so the file is the whole audience and not
   * just the first screen. Reports whether the safety stop cut it short, so the
   * export never passes off a partial file as the complete one.
   */
  const fetchEveryInteraction = async (announcementId: string) => {
    const all: AnnouncementInteraction[] = [];
    let expected = 0;

    for (let current = 1; current <= EXPORT_MAX_PAGES; current += 1) {
      // Sequential on purpose: each page tells us whether another one is needed.
      // eslint-disable-next-line no-await-in-loop
      const response = await client.query<TAnnouncementInteractionsQuery>({
        query: announcementInteractionsQuery,
        variables: { announcementId, page: current, perPage: PAGE_SIZE },
        fetchPolicy: "network-only"
      });

      const result = response.data?.announcement?.getAnnouncementInteractions;

      expected = result?.totalCount ?? 0;
      all.push(...(result?.records ?? []));

      if (all.length >= expected) break;
    }

    return { rows: all, missing: Math.max(expected - all.length, 0) };
  };

  const handleExport = async () => {
    if (!announcement) return;

    try {
      setExporting(true);

      const { rows, missing } = await fetchEveryInteraction(announcement.id);

      if (rows.length === 0) {
        addToast({ type: "info", title: t(AnnouncementMessages.interactionsEmpty) });

        return;
      }

      const stamp = format(new Date(), "yyyy-MM-dd");

      downloadCsv(`${toFileNameSlug(announcement.title)}-${stamp}.csv`, columns, rows);

      // The file is short of the full audience: say so, instead of letting it
      // pass as complete.
      if (missing > 0) {
        addToast({
          type: "error",
          title: t(AnnouncementMessages.interactionsExportPartial, { exported: rows.length, total: rows.length + missing })
        });
      }
    } catch {
      // Network/GraphQL errors are already toasted by the Apollo error link.
    } finally {
      setExporting(false);
    }
  };
  // endregion Handlers

  return (
    <ModalFormDialog
      open={Boolean(announcement)}
      title={t(AnnouncementMessages.interactionsTitle)}
      subtitle1={announcement?.title}
      onClose={onClose}
    >
      <Styled.Content>
        <Styled.Toolbar>
          <span>{t(AnnouncementMessages.interactionsCount, { count: totalCount })}</span>
          <Styled.ExportButton
            type="button"
            onClick={handleExport}
            disabled={exporting || loading || totalCount === 0}
          >
            <FileDownload fontSize="small" />
            {exporting ? t(AnnouncementMessages.interactionsExporting) : t(AnnouncementMessages.interactionsExport)}
          </Styled.ExportButton>
        </Styled.Toolbar>

        <Styled.ListHeader>
          <span>{t(AnnouncementMessages.interactionsUser)}</span>
          <span>{t(AnnouncementMessages.interactionsTenant)}</span>
          <span>{t(AnnouncementMessages.interactionsReadAt)}</span>
          <span>{t(AnnouncementMessages.interactionsClickedAt)}</span>
        </Styled.ListHeader>

        {loading && <StateMessage>{t(GlobalMessages.loading)}</StateMessage>}

        {!loading && records.length === 0 && (
          <StateMessage>{t(AnnouncementMessages.interactionsEmpty)}</StateMessage>
        )}

        {!loading && records.map((interaction) => (
          <Styled.ListRow key={interaction.userId}>
            <Styled.UserCell>
              <Styled.UserName>{interaction.userName}</Styled.UserName>
              <Styled.UserEmail>{interaction.userEmail}</Styled.UserEmail>
            </Styled.UserCell>
            <span>{interaction.tenantName}</span>
            <span>{formatOrDash(interaction.readAt)}</span>
            <span>{formatOrDash(interaction.clickedAt)}</span>
          </Styled.ListRow>
        ))}
      </Styled.Content>
    </ModalFormDialog>
  );
};

export default AnnouncementInteractionsDialog;
